/**
 * Consent gate for non-essential tags.
 *
 * The CompliOne widget (loaded from index.html) owns the UI and the record.
 * This module owns the CONSEQUENCE: nothing non-essential may run until the
 * visitor has actually said yes.
 *
 * Why this file exists at all: before it, `CookieBanner` wrote the choice to
 * localStorage, dispatched a `soltik-cookie-consent` event, and NOTHING
 * listened. Verified on production 2026-08-09 — clicking "Reject
 * non-essential" left `posthog.has_opted_out_capturing() === false`, i.e.
 * still capturing. The banner asked a question and ignored the answer.
 * Swapping in a nicer banner would not have changed that by itself.
 *
 * Granularity note: CompliOne's public API keys consent by TYPE
 * (`cookie` | `marketing` | `research` | `third-party`), not by the
 * categories shown in its modal — those toggles set `dataset.consentType`,
 * and the `categories` array in the project config is descriptive text.
 * So 'cookie' is the finest available signal for analytics, and a visitor
 * who accepts cookies at all is accepting PostHog.
 */

/** The consent type that governs analytics/session-replay tags. */
const ANALYTICS_TYPE = 'cookie';

/** Must match data-project-id in index.html. */
const PROJECT_ID = 'LwH6YGtuFAjWqfJlhDwCs';

type CompliOne = {
  hasConsent?: (typeId: string) => boolean;
};

function widget(): CompliOne | undefined {
  return (window as unknown as { CompliOne?: CompliOne }).CompliOne;
}

/**
 * Whether analytics may run RIGHT NOW.
 *
 * Reads localStorage directly rather than calling `CompliOne.hasConsent()`,
 * because the widget is loaded `async` and may not have parsed yet on a cold
 * navigation — and the honest answer while it is still loading is "no".
 * Absent or unparseable state is treated as refusal: consent must be given,
 * never assumed.
 */
export function hasAnalyticsConsent(): boolean {
  const api = widget();
  if (api?.hasConsent) {
    try { return api.hasConsent(ANALYTICS_TYPE) === true; } catch { /* fall through */ }
  }
  try {
    const raw = localStorage.getItem(`complione_consents_${PROJECT_ID}`);
    if (!raw) return false;
    return JSON.parse(raw)?.[ANALYTICS_TYPE]?.granted === true;
  } catch {
    return false;
  }
}

/**
 * Run `cb` once analytics consent is granted, now or later, and again if it
 * is later withdrawn.
 *
 * The widget exposes no DOM event — only an `onConsentChange` callback on the
 * instance it builds for itself during auto-init. So we wait for the instance
 * to appear, then attach. The poll is bounded: if the widget never loads
 * (blocked, CDN down, offline), we simply never grant, which is the correct
 * failure direction for a consent gate.
 */
export function onAnalyticsConsentChange(cb: (granted: boolean) => void): void {
  const KEY = `complione_consents_${PROJECT_ID}`;

  let last = hasAnalyticsConsent();
  let lastRaw = readRaw();
  if (last) cb(true);

  function readRaw(): string | null {
    try { return localStorage.getItem(KEY); } catch { return null; }
  }

  // Polls for the LIFETIME of the page, deliberately. An earlier version
  // stopped after 15s and that was a real bug, caught in-browser: consent
  // withdrawn later than that was never noticed, so PostHog kept capturing
  // after the visitor said stop — the exact failure this module exists to
  // prevent. Withdrawal has no deadline, so neither can the watcher.
  //
  // Nor is the widget's own `onConsentChange` enough on its own: it fires
  // from setConsent(), but the modal's "Save Preferences" path writes the
  // new state directly, so a withdrawal through the modal never invokes it.
  // Storage is the one place every path agrees on. `storage` events don't
  // fire in the tab that made the change, so this has to be a poll.
  //
  // Cost is a getItem plus a string compare per tick; the JSON parse only
  // happens on the ticks where the raw value actually changed.
  window.setInterval(() => {
    const raw = readRaw();
    if (raw === lastRaw) return;
    lastRaw = raw;

    const now = hasAnalyticsConsent();
    if (now !== last) {
      last = now;
      cb(now);
    }
  }, 500);
}
